/**
 * De ontwikkeling van één leerling: de score per toets in de tijd, als
 * lijn, en daaronder de geschiedenis.
 *
 * Sinds stap 24 staan daarin ook de beslissingen: een ander niveau (een
 * doorgetrokken lijn in de curve, met de reden als die bekend is: "samen
 * besloten na de papieren toets") en een papieren toets (gestippeld; zonder
 * score, want die gaat niet terug in de app, wel met wat leerkracht en
 * leerling na het nakijken samen besloten). Zo is te zien op welk moment een
 * leerling een stap hoger ging, en wat eraan voorafging. De tijdas loopt
 * daarom op datum, niet per toets.
 */
import React from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

const dagMaand = (waarde) => {
  const datum = new Date(waarde);
  return `${datum.getDate()}/${datum.getMonth() + 1}`;
};
const tijd = (iso) => new Date(iso).getTime();
const DAG = 24 * 3600 * 1000;

/** Alles wat er gebeurde, op volgorde van datum: toetsen in de app, een ander niveau, een papieren toets. */
export function geschiedenis(leerling) {
  const regels = [
    ...(leerling.datapunten || []).map(p => ({ soort: 'toets', datum: p.datum, ...p })),
    ...(leerling.niveauwijzigingen || []).map(w => ({ soort: 'niveau', ...w })),
    ...(leerling.papieren_toetsen || []).map(t => ({ soort: 'papier', ...t })),
  ].filter(r => r.datum);
  return regels.sort((a, b) => tijd(a.datum) - tijd(b.datum));
}

export function Groeicurve({ leerling }) {
  const punten = (leerling.datapunten || []).map(p => ({ ...p, tijd: tijd(p.datum) }));
  const regels = geschiedenis(leerling);
  const momenten = regels.map(r => tijd(r.datum));
  const domein = momenten.length ? [Math.min(...momenten) - DAG, Math.max(...momenten) + DAG] : ['auto', 'auto'];
  return (
    <>
      {punten.length > 0 && (
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={punten}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="tijd" type="number" scale="time" domain={domein} tick={{ fontSize: 12 }} tickFormatter={dagMaand} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(w) => `${w}%`} />
              <Tooltip
                formatter={(w) => [`${w}%`, 'Score']}
                labelFormatter={(label) => {
                  const punt = punten.find(d => d.tijd === label);
                  return punt ? `${punt.text_titel} (${punt.niveau})` : dagMaand(label);
                }}
              />
              {regels.filter(r => r.soort === 'niveau').map((r, i) => (
                <ReferenceLine key={`n${i}`} x={tijd(r.datum)} stroke="var(--goed-2)" strokeWidth={2}
                  label={{ value: r.naar, position: 'insideTopRight', fontSize: 11, fill: 'var(--goed-2)' }} />
              ))}
              {regels.filter(r => r.soort === 'papier').map((r, i) => (
                <ReferenceLine key={`p${i}`} x={tijd(r.datum)} stroke="var(--tekst-3)" strokeDasharray="4 4"
                  label={{ value: 'papier', position: 'insideBottomRight', fontSize: 10, fill: 'var(--tekst-3)' }} />
              ))}
              <Line type="monotone" dataKey="mc_percentage" stroke="var(--accent)" strokeWidth={2}
                dot={{ fill: 'var(--accent)', strokeWidth: 2 }} name="Score %" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {punten.length > 0 && regels.some(r => r.soort !== 'toets') && (
        <p className="text-muted text-small">
          Doorgetrokken lijn: een ander niveau (beslissing van de leerkracht). Gestippeld: een papieren toets.
        </p>
      )}

      {regels.length > 0 && (
        <>
          <h4 className="mt-16 mb-8">Toetsgeschiedenis en beslissingen</h4>
          <table data-testid="geschiedenis">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Wat</th>
                <th>Niveau</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {regels.map((r, i) => (
                <tr key={i} style={r.soort === 'niveau' ? { background: 'var(--goed-vlak)' } : undefined}>
                  <td>{new Date(r.datum).toLocaleDateString('nl-NL')}</td>
                  {r.soort === 'toets' && (
                    <>
                      <td>{r.text_titel}</td>
                      <td><span className="badge badge-primary">{r.niveau}</span></td>
                      <td>
                        <strong>{r.mc_percentage}%</strong>
                        <span className="text-muted" style={{ marginLeft: 8 }}>({r.close_reading_score}/{r.close_reading_max})</span>
                      </td>
                    </>
                  )}
                  {r.soort === 'niveau' && (
                    <>
                      <td>
                        <strong>Niveau {r.van ? `${r.van} → ` : ''}{r.naar}</strong>
                        {(r.door || r.reden) && <span className="text-muted"> ({[r.door, r.reden].filter(Boolean).join('; ')})</span>}
                      </td>
                      <td><span className="badge badge-success">{r.naar}</span></td>
                      <td className="text-muted">beslissing</td>
                    </>
                  )}
                  {r.soort === 'papier' && (
                    <>
                      <td>Papieren toets: {r.titel}</td>
                      <td><span className="badge badge-primary">{r.niveau}</span></td>
                      <td className="text-muted">
                        op papier
                        {r.besluit === 'hoger' && <>; samen besloten: naar {r.niveau}</>}
                        {r.besluit === 'blijft' && <>; samen besloten: nog op {r.niveau_toen || '-'}</>}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
